<template>
  <div class="test-case-editor">
    <div class="editor-content">
      <!-- 左侧配置面板 -->
      <div class="element-library">
        <div class="panel-header">
          <div class="panel-title">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="panel-icon">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
            </svg>
            <span>测试用例配置</span>
          </div>
          <div style="display: flex; align-items: center; gap: 12px;">
            <button class="btn-history" @click="showHistoryDialog = true">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              历史数据
            </button>
            <div v-if="loading" class="generating-badge">
              <div class="generating-dot"></div>
              AI 生成中
            </div>
          </div>
        </div>

        <div class="ai-form-panel">
          <el-form :model="form" :rules="rules" ref="testCaseForm" label-position="top">
            <el-form-item label="课题名称" prop="projectName">
              <el-input v-model="form.projectName" placeholder="例如：在线购物系统" clearable />
            </el-form-item>

            <el-form-item label="测试模块" required>
              <div class="module-tags">
                <el-tag
                  v-for="(module, index) in form.moduleList"
                  :key="index"
                  size="medium"
                  closable
                  @close="removeModule(index)"
                  style="margin: 0 4px 4px 0; font-size: 14px;"
                >{{ module }}</el-tag>
                <div v-if="moduleInputVisible" style="display:inline-block;">
                  <el-input
                    ref="moduleInput"
                    v-model="moduleInputValue"
                    size="small"
                    style="width: 120px;"
                    placeholder="输入模块名"
                    @keyup.enter.native="confirmModule"
                    @blur="confirmModule"
                  />
                </div>
                <el-button v-else type="primary" size="small" @click="showModuleInput">+ 新增模块</el-button>
              </div>
              <div class="save-reminder" style="margin: 6px 0 0 0;padding: 1px 15px;">💡 每个模块单独添加，有几个模块就写几个</div>
            </el-form-item>

            <div style="display: flex; gap: 12px;">
              <el-form-item label="表格起始编号" prop="tableStartNumber" style="flex: 1; margin-bottom: 18px;">
                <el-input v-model="form.tableStartNumber" placeholder="例如：6-1" clearable />
              </el-form-item>

              <el-form-item label="测试用例数量" style="flex: 1; margin-bottom: 18px;">
                <el-select v-model="form.caseCount" placeholder="选择生成数量" style="width: 100%;">
                  <el-option label="2个" :value="2"></el-option>
                  <el-option label="3个" :value="3"></el-option>
                  <el-option label="4个" :value="4"></el-option>
                  <el-option label="5个" :value="5"></el-option>
                  <el-option label="6个" :value="6"></el-option>
                  <el-option label="7个" :value="7"></el-option>
                </el-select>
              </el-form-item>
            </div>

            <el-form-item label="功能描述">
              <textarea
                v-model="form.description"
                class="func-editor"
                placeholder="描述系统的主要功能和测试重点，例如：
用户登录功能：
- 正确的用户名和密码可以登录
- 错误的用户名或密码提示错误
- 空用户名或密码提示必填
- 记住密码功能
- 验证码验证"
              />
            </el-form-item>

            <el-form-item label="测试用例表头配置">
              <div class="column-config">
                <div class="column-header">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="column-icon">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                  <span>列标题 ({{ form.fields.length }}/8)</span>
                  <button type="button" class="btn-add-column" @click="addField" :disabled="form.fields.length >= 8">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                    </svg>
                  </button>
                </div>
                <div class="column-list">
                  <div v-for="(field, index) in form.fields" :key="index" class="column-item">
                    <div class="column-row">
                      <span class="column-number">{{ index + 1 }}</span>
                      <el-input :value="field.title" @input="updateFieldTitle(index, $event)" placeholder="输入列标题" size="small" class="column-title-input" />
                      <button type="button" class="btn-remove-column" @click="removeField(index)">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </el-form-item>
          </el-form>
        </div>

        <div class="panel-footer">
          <button class="btn-ai btn-generate" :disabled="loading" @click="generate">
            <span class="btn-content">
              <img src="@/assets/ai-logo.png" alt="AI" class="ai-icon" />
              {{ loading ? 'AI 生成中...' : 'AI 生成测试用例' }}
            </span>
            <span class="btn-points">（消耗{{ pointsConfig.testCase }}积分）</span>
          </button>
        </div>
      </div>

      <!-- 右侧结果区域 -->
      <div class="result-area">
        <div class="result-header">
          <div class="result-title">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="panel-icon">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
            </svg>
            生成结果
          </div>
          <div class="result-meta">
            <span class="case-count" v-if="sections.length > 0">共 {{ sections.length }} 个用例</span>
            <button class="btn-export" @click="exportWord()" :disabled="sections.length === 0">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="15" height="15">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              导出 Word
            </button>
          </div>
        </div>

        <div class="result-body">
          <!-- 加载中 -->
          <div class="loading-state" v-if="loading && sections.length === 0">
            <div class="loading-spinner"></div>
            <p>AI 正在生成测试用例，请稍候...</p>
            <p class="loading-sub" v-if="progress">{{ progress }}</p>
          </div>

          <!-- 空状态 -->
          <div class="empty-state" v-else-if="sections.length === 0">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="48" height="48">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
            </svg>
            <p>填写左侧信息后点击「AI 生成测试用例」</p>
          </div>

          <!-- 测试用例列表 -->
          <div class="test-cases-list" v-else>
            <div v-for="(section, index) in sections" :key="index" class="test-case-row">
              <div class="test-case-content">
                <!-- 显示三线表格式 -->
                <p v-if="section.description" class="table-description">{{ section.description }}</p>
                <h3 v-if="section.title" class="table-title">{{ section.title }}</h3>
                <table v-if="section.rows && section.rows.length > 0" class="three-line-table">
                  <thead>
                    <tr>
                      <th v-for="(value, key) in section.rows[0]" :key="key">{{ key }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, rowIndex) in section.rows" :key="rowIndex">
                      <td v-for="(value, key) in row" :key="key">{{ value }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 历史数据弹框 -->
    <el-dialog
      title="历史数据"
      :visible.sync="showHistoryDialog"
      width="1000px"
    >
      <div class="history-content">
        <div class="history-description" style="margin-bottom: 12px;">
          <ul>
            <li>• 共有 {{ savedTestCases.length }} 个已保存的测试用例（按保存时间倒序排列）</li>
            <li>• 点击"加载"按钮可以加载对应的测试用例</li>
            <li>• 系统最多保存20条历史数据记录</li>
          </ul>
        </div>

        <el-table
          :data="paginatedTestCases"
          stripe
          style="width: 100%"
          empty-text="暂无保存的历史数据"
        >
          <el-table-column type="index" label="序号" width="60" align="center">
            <template slot-scope="scope">
              {{ (currentPage - 1) * pageSize + scope.$index + 1 }}
            </template>
          </el-table-column>
          
          <el-table-column label="课题名称" min-width="200" align="center">
            <template slot-scope="scope">
              <span class="diagram-name" :class="{ current: currentTestCaseId === scope.row.id }">
                {{ scope.row.projectName || '-' }}
              </span>
            </template>
          </el-table-column>
          
          <el-table-column label="模块数" width="80" align="center">
            <template slot-scope="scope">
              <span>{{ scope.row.moduleList ? scope.row.moduleList.length : 0 }}</span>
            </template>
          </el-table-column>
          
          <el-table-column label="用例数" width="80" align="center">
            <template slot-scope="scope">
              <span>{{ scope.row.sections ? scope.row.sections.length : 0 }}</span>
            </template>
          </el-table-column>
          
          <el-table-column label="保存时间" width="180" align="center">
            <template slot-scope="scope">
              <div class="absolute-time">{{ formatTime(scope.row.timestamp) }}</div>
            </template>
          </el-table-column>
          
          <el-table-column label="操作" width="180" align="center" fixed="right">
            <template slot-scope="scope">
              <div class="action-buttons">
                <el-button size="mini" type="primary" @click="loadTestCase(scope.row)">加载</el-button>
                <el-button size="mini" type="danger" @click="deleteTestCase(scope.row)">删除</el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-container">
          <el-pagination
            :current-page.sync="currentPage"
            :page-size="pageSize"
            :total="savedTestCases.length"
            @current-change="handlePageChange"
            layout="prev, pager, next" />
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import request, { baseURL, getHeaders } from '@/utils/request'
import {
  AlignmentType,
  BorderStyle,
  Document,
  HeadingLevel,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  VerticalAlign,
  WidthType,
} from 'docx'

export default {
  name: 'TestCase',
  data() {
    return {
      moduleInputVisible: false,
      moduleInputValue: '',
      showHistoryDialog: false,
      savedTestCases: [],
      currentTestCaseId: null,
      currentPage: 1,
      pageSize: 10,
      pointsConfig: {
        testCase: 4
      },
      rules: {
        projectName: [{ required: true, message: '请输入课题名称', trigger: 'blur' }],
        tableStartNumber: [{ required: true, message: '请输入表格起始编号', trigger: 'blur' }]
      }
    }
  },
  created() {
    this.increasePageView()
    this.loadPointsConfig()
    this.loadHistoryFromLocal()
  },
  mounted() {
    // 默认加载最近一个历史记录
    this.loadLatestTestCase()
  },
  computed: {
    form: {
      get() {
        return this.$store.state.testCase.form
      },
      set(val) {
        this.$store.commit('setTestCaseForm', val)
      }
    },
    loading: {
      get() {
        return this.$store.state.testCase.loading
      },
      set(val) {
        this.$store.commit('setTestCaseLoading', val)
      }
    },
    progress: {
      get() {
        return this.$store.state.testCase.progress
      },
      set(val) {
        this.$store.commit('setTestCaseProgress', val)
      }
    },
    sections: {
      get() {
        return this.$store.state.testCase.sections
      },
      set(val) {
        this.$store.commit('setTestCaseSections', val)
      }
    },
    paginatedTestCases() {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.savedTestCases.slice(start, end)
    }
  },
  methods: {
    // 加载积分配置
    async loadPointsConfig() {
      try {
        const response = await this.request.get('/ai/points-config')
        if (response.code === '200' && response.data) {
          this.pointsConfig = response.data
        }
      } catch (error) {
        console.error('加载积分配置失败:', error)
      }
    },
    
    increasePageView() {
      this.request.get('/pageView/increase', {
        params: { pageName: 'testCase' }
      }).catch(() => {})
    },
    // 显示模块输入框
    showModuleInput() {
      this.moduleInputVisible = true
      this.$nextTick(() => {
        this.$refs.moduleInput && this.$refs.moduleInput.focus()
      })
    },
    
    // 确认添加模块
    confirmModule() {
      const val = this.moduleInputValue.trim()
      if (val && !this.form.moduleList.includes(val)) {
        const newForm = { ...this.form }
        newForm.moduleList = [...newForm.moduleList, val]
        this.$store.commit('setTestCaseForm', newForm)
      }
      this.moduleInputVisible = false
      this.moduleInputValue = ''
    },
    
    // 删除模块
    removeModule(index) {
      const newForm = { ...this.form }
      newForm.moduleList = newForm.moduleList.filter((_, i) => i !== index)
      this.$store.commit('setTestCaseForm', newForm)
    },

    // 历史记录相关方法
    loadHistoryFromLocal() {
      const history = JSON.parse(localStorage.getItem('testCaseHistory') || '[]')
      this.savedTestCases = history
    },
    
    loadLatestTestCase() {
      if (this.savedTestCases.length === 0) return
      
      const latest = this.savedTestCases[0]
      this.loadTestCaseData(latest)
    },
    
    loadTestCaseData(testCase) {
      // 加载表单数据
      this.form = {
        projectName: testCase.projectName,
        moduleList: testCase.moduleList,
        description: testCase.description,
        caseCount: testCase.caseCount,
        fields: testCase.fields,
        tableStartNumber: testCase.tableStartNumber
      }
      
      // 加载生成的内容
      if (testCase.sections) {
        this.sections = testCase.sections
      }
      
      this.currentTestCaseId = testCase.id
    },
    
    saveToHistory() {
      const testCase = {
        id: Date.now(),
        timestamp: Date.now(),
        projectName: this.form.projectName,
        moduleList: this.form.moduleList,
        description: this.form.description,
        caseCount: this.form.caseCount,
        fields: this.form.fields,
        tableStartNumber: this.form.tableStartNumber,
        sections: this.sections
      }
      
      const history = JSON.parse(localStorage.getItem('testCaseHistory') || '[]')
      history.unshift(testCase)
      
      // 只保留最近20条
      if (history.length > 20) {
        history.splice(20)
      }
      
      localStorage.setItem('testCaseHistory', JSON.stringify(history))
      this.savedTestCases = history
      this.currentTestCaseId = testCase.id
    },
    
    deleteTestCase(testCase) {
      this.$confirm('确定要删除这个测试用例吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const history = JSON.parse(localStorage.getItem('testCaseHistory') || '[]')
        const index = history.findIndex(item => item.id === testCase.id)
        if (index > -1) {
          history.splice(index, 1)
          localStorage.setItem('testCaseHistory', JSON.stringify(history))
          this.savedTestCases = history
          this.$message.success('删除成功')
        }
      }).catch(() => {})
    },
    
    loadTestCase(testCase) {
      this.$confirm('确定要加载这个测试用例吗？当前内容将被替换。', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.loadTestCaseData(testCase)
        this.showHistoryDialog = false
        this.$message.success('测试用例加载成功')
      }).catch(() => {})
    },
    
    handlePageChange(page) {
      this.currentPage = page
    },
    
    formatTime(timestamp) {
      const date = new Date(timestamp)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },

    // 添加字段
    addField() {
      if (this.form.fields.length < 8) {
        const newForm = { ...this.form }
        newForm.fields = [...newForm.fields, { title: '' }]
        this.$store.commit('setTestCaseForm', newForm)
      }
    },

    // 删除字段
    removeField(index) {
      if (this.form.fields.length > 1) {
        const newForm = { ...this.form }
        newForm.fields = newForm.fields.filter((_, i) => i !== index)
        this.$store.commit('setTestCaseForm', newForm)
      }
    },

    // 更新字段标题
    updateFieldTitle(index, value) {
      const newForm = { ...this.form }
      newForm.fields = newForm.fields.map((f, i) => 
        i === index ? { ...f, title: value } : f
      )
      this.$store.commit('setTestCaseForm', newForm)
    },

    async generate() {
      if (this.form.moduleList.length === 0) {
        this.$message.warning('请至少添加一个测试模块')
        return
      }

      if (this.form.fields.length === 0) {
        this.$message.warning('请至少添加一个字段')
        return
      }

      const emptyField = this.form.fields.find(f => !f.title || f.title.trim() === '')
      if (emptyField) {
        this.$message.warning('请填写所有字段标题，不能为空')
        return
      }

      if (!this.form.tableStartNumber || !this.form.tableStartNumber.trim()) {
        this.$message.warning('请输入表格起始编号')
        return
      }

      // 添加积分确认提示
      try {
        await this.$confirm(`此操作将消耗${this.pointsConfig.testCase}积分，是否继续？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch {
        this.$message.info('已取消操作')
        return
      }

      this.loading = true
      this.sections = []
      this.progress = ''
      
      // 保存表单数据到store
      this.$store.commit('setTestCaseForm', this.form)

      try {
        // 使用 axios 发送 JSON 格式数据（自动加密）
        const requestData = {
          projectName: this.form.projectName,
          modules: this.form.moduleList.join(','),
          description: this.form.description || '',
          caseCount: this.form.caseCount,
          fields: this.form.fields,  // 直接传数组，后端会处理
          tableStartNumber: this.form.tableStartNumber || ''
        }

        const result = await request.post('/ai/generateTestCase', requestData)
        
        if (result.code === '200' && result.data) {
          const testCases = result.data.testCases || []
          
          // 直接使用后端返回的三线表格式数据并保存到store
          if (testCases.length > 0) {
            this.$store.commit('setTestCaseSections', testCases)
            this.$message.success('测试用例生成完成')
            // 保存到历史记录
            this.saveToHistory()
            // 刷新积分
            this.refreshPoints()
          } else {
            this.$message.warning('未能生成测试用例，请重试')
          }
        } else {
          throw new Error(result.msg || '生成失败')
        }
      } catch (e) {
        this.$message.error('生成失败：' + e.message)
      } finally {
        this.loading = false
      }
    },
    
    // 刷新积分
    refreshPoints() {
      // 使用全局方法刷新积分
      this.$refreshPoints()
    },

    async exportWord() {
      if (this.sections.length === 0) return
      
      try {
        const children = []
        
        this.sections.forEach((section, index) => {
          // 添加描述
          if (section.description) {
            children.push(
              new Paragraph({
                text: section.description,
                spacing: {
                  before: index === 0 ? 0 : 400,
                  after: 200,
                },
                alignment: AlignmentType.LEFT,
              })
            )
          }
          
          // 添加标题（作为普通段落，居中）
          if (section.title) {
            children.push(
              new Paragraph({
                text: section.title,
                spacing: {
                  before: 0,
                  after: 200,
                },
                alignment: AlignmentType.CENTER,
              })
            )
          }
          
          // 创建表格
          if (section.rows && section.rows.length > 0) {
            // 获取表头
            const headers = Object.keys(section.rows[0])
            
            // 创建表头单元格
            const headerCells = headers.map((header, index) => {
              let cellWidth = undefined
              if (header === '编号') {
                cellWidth = { size: 1134, type: WidthType.DXA } // 2cm = 1134 DXA
              } else if (index === 1) {
                cellWidth = { size: 1701, type: WidthType.DXA } // 3cm = 1701 DXA
              }
              return new TableCell({
                borders: {
                  bottom: { style: BorderStyle.SINGLE, size: 6, color: "000000" },
                  top: { style: BorderStyle.NONE },
                  left: { style: BorderStyle.NONE },
                  right: { style: BorderStyle.NONE },
                },
                children: [new Paragraph({
                  text: header,
                  alignment: AlignmentType.CENTER,
                })],
                verticalAlign: VerticalAlign.CENTER,
                width: cellWidth,
              })
            })
            
            // 创建数据行
            const dataRows = section.rows.map((row, rowIndex) => {
              const cells = headers.map((header, index) => {
                let cellWidth = undefined
                if (header === '编号') {
                  cellWidth = { size: 1134, type: WidthType.DXA } // 2cm = 1134 DXA
                } else if (index === 1) {
                  cellWidth = { size: 1701, type: WidthType.DXA } // 3cm = 1701 DXA
                }
                return new TableCell({
                  borders: {
                    top: { style: BorderStyle.NONE },
                    bottom: { style: BorderStyle.NONE },
                    left: { style: BorderStyle.NONE },
                    right: { style: BorderStyle.NONE },
                  },
                  children: [new Paragraph({
                    text: String(row[header] || ''),
                    alignment: AlignmentType.CENTER,
                  })],
                  verticalAlign: VerticalAlign.CENTER,
                  width: cellWidth,
                })
              })
              
              // 最后一行添加底部边框
              if (rowIndex === section.rows.length - 1) {
                return new TableRow({
                  children: cells,
                  borders: {
                    bottom: { style: BorderStyle.SINGLE, size: 11, color: "000000" },
                  },
                })
              }
              
              return new TableRow({ children: cells })
            })
            
            // 创建三线表
            const table = new Table({
              width: { size: 100, type: WidthType.PERCENTAGE },
              borders: {
                top: { style: BorderStyle.SINGLE, size: 11, color: "000000" },
                bottom: { style: BorderStyle.SINGLE, size: 11, color: "000000" },
                left: { style: BorderStyle.NONE },
                right: { style: BorderStyle.NONE },
                insideHorizontal: { style: BorderStyle.NONE },
                insideVertical: { style: BorderStyle.NONE },
              },
              rows: [
                new TableRow({
                  tableHeader: true,
                  children: headerCells,
                  borders: {
                    top: { style: BorderStyle.SINGLE, size: 11, color: "000000" },
                    bottom: { style: BorderStyle.SINGLE, size: 6, color: "000000" },
                  },
                }),
                ...dataRows,
              ],
            })
            
            children.push(table)
          }
          
          // 添加间距
          children.push(
            new Paragraph({
              spacing: {
                before: 400,
                after: 400,
              },
            })
          )
        })
        
        const doc = new Document({
          sections: [{
            properties: {
              page: {
                margin: {
                  top: 1440,
                  right: 1440,
                  bottom: 1440,
                  left: 1440,
                },
              },
            },
            children: children,
          }],
          styles: {
            paragraphStyles: [
              {
                id: "Heading3",
                name: "Heading 3",
                basedOn: "Normal",
                next: "Normal",
                quickFormat: true,
                run: {
                  size: 21,
                  bold: true,
                  font: "宋体",
                },
                paragraph: {
                  spacing: {
                    before: 400,
                    after: 200,
                  },
                },
              },
              {
                id: "Normal",
                name: "Normal",
                basedOn: "Normal",
                next: "Normal",
                run: {
                  size: 21,
                  font: "宋体",
                },
                paragraph: {
                  spacing: {
                    line: 276,
                  },
                },
              },
            ],
          },
        })
        
        const blob = await Packer.toBlob(doc)
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = (this.form.projectName || '测试用例') + '.docx'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        
        this.$message.success('导出成功')
      } catch (e) {
        console.error('导出失败:', e)
        this.$message.error('导出失败：' + e.message)
      }
    }
  }
}
</script>

<style scoped>
.test-case-editor {
  height: calc(100vh - 118px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.editor-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* 左侧配置面板 */
.element-library {
  width: 380px;
  background: #fff;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  z-index: 1;
  overflow: hidden;
  border-right: 1px solid #e5e7eb;
}

.panel-header {
  padding: 12px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 56px;
}

.generating-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #10b981;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 12px;
  padding: 3px 10px;
}

.generating-dot {
  width: 7px;
  height: 7px;
  background: #10b981;
  border-radius: 50%;
  animation: blink 1s step-end infinite;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

.panel-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.panel-icon {
  width: 22px;
  height: 22px;
  color: #4f46e5;
}

.ai-form-panel {
  padding: 16px;
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.module-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

.save-reminder {
  font-size: 14px;
  color: #856404;
  background: #fff3cd;
  padding: 6px 14px;
  border-radius: 4px;
}

.func-editor {
  width: 100%;
  height: 220px;
  background: #ffffff;
  color: #333;
  font-family: inherit;
  font-size: 13px;
  padding: 8px 12px;
  resize: none;
  border: 1px solid #dcdfe6;
  outline: none;
  line-height: 1.6;
  border-radius: 4px;
  box-sizing: border-box;
}

.func-editor:focus { border-color: #4f46e5; }

.panel-footer {
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.btn-ai {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px;
  font-size: 16px;
  background: #10b981;
  color: #fff;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-ai:hover:not(:disabled) { background: #059669; }
.btn-ai:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-points {
  font-size: 16px;
  font-weight: 600;
  opacity: 0.9;
  white-space: nowrap;
}

.ai-icon {
  width: 26px;
  height: 26px;
  object-fit: contain;
}

/* 右侧结果区域 */
.result-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f9fafb;
  margin-left: 10px;
  border-left: 1px solid #d1d5db;
}

.result-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
  flex-shrink: 0;
  min-height: 56px;
}

.result-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.case-count {
  font-size: 14px;
  color: #6b7280;
  background: #f3f4f6;
  padding: 2px 8px;
  border-radius: 10px;
}

.btn-export {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  font-size: 13px;
  color: #fff;
  background: #4f46e5;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-export:hover:not(:disabled) { background: #4338ca; }
.btn-export:disabled { opacity: 0.4; cursor: not-allowed; }

.result-meta {
  display: flex;
  align-items: center;
  gap: 10px;
}

.result-body {
  flex: 1;
  overflow-y: auto;
  background: #fff;
  font-family: 宋体, SimSun, sans-serif;
  display: flex;
  flex-direction: column;
}

.loading-state, .empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 20px;
  background: #fafafa;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top-color: #4f46e5;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.loading-state p {
  font-size: 14px;
  font-weight: normal;
  margin: 0;
  color: inherit;
}

.loading-sub { 
  font-size: 12px; 
  color: #9ca3af; 
}

.empty-state svg { 
  color: #d1d5db;
}

.empty-state p { 
  font-size: 14px;
  font-weight: normal;
  margin: 0;
  text-align: center;
  line-height: 1.6;
  color: #333;
}

/* 测试用例列表 */
.test-cases-list {
  padding: 24px 32px;
  font-family: 宋体, SimSun, sans-serif;
}

.test-case-row {
  margin-bottom: 40px;
  padding-bottom: 0;
  border-bottom: none;
}

.test-case-row:last-child {
  margin-bottom: 0;
}

.test-case-content {
  background: transparent;
  padding: 0;
  border-radius: 0;
  border: none;
}

.test-case-content .table-description {
  font-size: 14px;
  color: #000;
  margin: 0 0 12px 0;
  line-height: 1.8;
}

.test-case-content .table-title {
  font-size: 14px;
  font-weight: normal;
  margin: 0 0 16px 0;
  text-align: center;
  color: #000;
}

.test-case-content .three-line-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  color: #000;
  table-layout: fixed;
}

.test-case-content .three-line-table thead tr {
  border-top: 1.5px solid #000;
  border-bottom: 1px solid #000;
}

.test-case-content .three-line-table thead th {
  padding: 10px 12px;
  text-align: center;
  vertical-align: middle;
  border: none;
  font-weight: normal;
  color: #000;
}

.test-case-content .three-line-table thead th:first-child {
  width: 76px; /* 2厘米 ≈ 76px */
}

.test-case-content .three-line-table thead th:nth-child(2) {
  width: 113px; /* 3厘米 ≈ 113px */
}

.test-case-content .three-line-table tbody tr:last-child {
  border-bottom: 1.5px solid #000;
}

.test-case-content .three-line-table tbody td {
  padding: 10px 12px;
  text-align: center;
  vertical-align: middle;
  border: none;
  color: #000;
}

.test-case-content .three-line-table tbody td:first-child {
  width: 76px; /* 2厘米 ≈ 76px */
}

.test-case-content .three-line-table tbody td:nth-child(2) {
  width: 113px; /* 3厘米 ≈ 113px */
}

.column-config {
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.column-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.column-icon {
  width: 18px;
  height: 18px;
  color: #6b7280;
}

.btn-add-column {
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  background: #4f46e5;
  color: #fff;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-add-column:hover:not(:disabled) {
  background: #4338ca;
}

.btn-add-column:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.column-list {
  padding: 12px;
}

.column-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.column-item:last-child {
  margin-bottom: 0;
}

.column-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.column-title-input {
  flex: 1;
}

.column-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: #e5e7eb;
  color: #6b7280;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
}

.btn-remove-column {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  background: #ef4444;
  color: #fff;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
  flex-shrink: 0;
}

.btn-remove-column:hover {
  background: #dc2626;
}

.btn-history {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 13px;
  color: #4f46e5;
  background: #fff;
  border: 1px solid #4f46e5;
  border-radius: 0;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-history:hover {
  background: #4f46e5;
  color: #fff;
}

.btn-history svg {
  width: 16px;
  height: 16px;
}

.example-container {
  padding: 24px 32px;
  font-family: 宋体, SimSun, sans-serif;
}

.example-table {
  margin-bottom: 40px;
}

.example-table:last-child {
  margin-bottom: 0;
}

.table-description {
  font-size: 14px;
  color: #000;
  margin: 0 0 12px 0;
  line-height: 1.8;
}

.table-title {
  font-size: 14px;
  font-weight: normal;
  margin: 0 0 16px 0;
  text-align: center;
  color: #000;
}

.three-line-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  color: #000;
  table-layout: fixed;
}

.three-line-table thead tr {
  border-top: 1.5px solid #000;
  border-bottom: 1px solid #000;
}

.three-line-table thead th {
  padding: 10px 12px;
  text-align: center;
  vertical-align: middle;
  border: none;
  font-weight: normal;
  color: #000;
}

.three-line-table thead th:first-child {
  width: 76px; /* 2厘米 ≈ 76px */
}

.three-line-table thead th:nth-child(2) {
  width: 113px; /* 3厘米 ≈ 113px */
}

.three-line-table tbody tr:last-child {
  border-bottom: 1.5px solid #000;
}

.three-line-table tbody td {
  padding: 10px 12px;
  text-align: center;
  vertical-align: middle;
  border: none;
  color: #000;
}

.three-line-table tbody td:first-child {
  width: 76px; /* 2厘米 ≈ 76px */
}

.three-line-table tbody td:nth-child(2) {
  width: 113px; /* 3厘米 ≈ 113px */
}

/* 历史记录对话框样式 */
.history-content {
  padding: 0;
}

.history-description {
  background: #f9fafb;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
}

.history-description ul {
  margin: 0;
  padding-left: 20px;
  list-style: none;
}

.history-description li {
  color: #6b7280;
  font-size: 14px;
  line-height: 1.8;
}

.diagram-name {
  font-weight: 500;
  color: #111827;
}

.diagram-name.current {
  color: #2563eb;
  font-weight: 600;
}

.absolute-time {
  font-size: 13px;
  color: #6b7280;
}

.action-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding: 16px 0;
}
</style>
